import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Directionality } from '@angular/cdk/bidi';
import { ConnectedOverlayPositionChange, ConnectedPosition } from '@angular/cdk/overlay';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { StructuredProcessStateService } from '../../moov-common/services/chat/structured-process-state.service';
import {
  StepTypeEnum,
  StructuredProcessData,
  StructuredProcessInstance,
  StructuredProcessInstanceStatusEnum,
} from '../../moov-common/services/chat/strcutredProcess-data';

interface MergedStep {
  id: string;
  idx: number;
  header: string;
  stepType: StepTypeEnum;
  status: StructuredProcessInstanceStatusEnum;
}

interface MergedStage {
  id: string;
  idx: number;
  name: string;
  status: StructuredProcessInstanceStatusEnum;
  steps: MergedStep[];
}

@Component({
  selector: 'app-sidebar-process-steps',
  templateUrl: './sidebar-process-steps.component.html',
  styleUrls: ['./sidebar-process-steps.component.scss'],
})
export class SidebarProcessStepsComponent implements OnInit, OnDestroy {
  @Output() stepClicked = new EventEmitter<void>();

  processData: StructuredProcessData | null = null;
  processInstance: StructuredProcessInstance | null = null;
  currentStageId = '';
  currentStepId = '';
  isB2C = false;
  expandedStageId = '';
  showCVUpload = false;
  cvUploadAfterStepId = '';
  isArtifactViewAvailable = false;
  isGeneratingArtifact = false;
  isPresentingArtifactView = false;
  isOnPersonalView = false;

  isNewQuestCardOpen = false;
  isNewQuestCardBelow = false;

  // above the button, or below it when there is no room above
  readonly newQuestCardPositions: ConnectedPosition[] = [
    { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -12 },
    { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 12 },
  ];

  statusEnum = StructuredProcessInstanceStatusEnum;
  stepTypeEnum = StepTypeEnum;

  private destroy$ = new Subject<void>();
  private newQuestCardTimer?: ReturnType<typeof setTimeout>;

  constructor(private stateService: StructuredProcessStateService, private router: Router, private dir: Directionality) {}

  ngOnInit(): void {
    this.isOnPersonalView = this.router.url.startsWith('/personalView');
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event) => {
      this.isOnPersonalView = (event as NavigationEnd).urlAfterRedirects.startsWith('/personalView');
    });

    this.stateService.processData$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.processData = data;
      this.updateCvUploadAnchor();
    });

    this.stateService.processInstance$.pipe(takeUntil(this.destroy$)).subscribe((instance) => {
      this.processInstance = instance;
    });

    this.stateService.currentStageId$.pipe(takeUntil(this.destroy$)).subscribe((id) => {
      this.currentStageId = id;
      this.expandedStageId = id;
    });

    this.stateService.currentStepId$.pipe(takeUntil(this.destroy$)).subscribe((id) => {
      this.currentStepId = id;
    });

    this.stateService.isB2C$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.isB2C = val;
      this.updateCvUploadAnchor();
    });

    this.stateService.forceStageExpand$.pipe(takeUntil(this.destroy$)).subscribe((stageId) => {
      this.expandedStageId = stageId;
    });

    this.stateService.showCVUpload$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.showCVUpload = val;
    });

    this.stateService.isArtifactViewAvailable$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.isArtifactViewAvailable = val;
    });

    this.stateService.isGeneratingArtifact$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.isGeneratingArtifact = val;
      if (val) {
        // a disabled button gets no mouseleave, so the card would come back on its own once the plan is ready
        this.hideNewQuestCard();
      }
    });

    this.stateService.isPresentingArtifactView$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.isPresentingArtifactView = val;
    });
  }

  get mergedStages(): MergedStage[] {
    if (!this.processData?.stages?.length || !this.processInstance?.stagesInstances?.length) {
      return [];
    }

    return this.processData.stages.map((stage) => {
      const stageInstance = this.processInstance!.stagesInstances.find((si) => si.stageId === stage.id);
      return {
        ...stage,
        status: stageInstance?.status ?? StructuredProcessInstanceStatusEnum.PENDING,
        steps: stage.steps.map((step) => {
          const stepInstance = stageInstance?.stepsInstances.find((si) => si.stepId === step.id);
          return {
            ...step,
            status: stepInstance?.status ?? StructuredProcessInstanceStatusEnum.PENDING,
          };
        }),
      };
    });
  }

  // Below the workplan button we render the last step of the penultimate stage
  // ("Ready to Go") as a top-level item, followed by the final stage
  // ("Make It Happen") as a regular collapsible stage.
  get mainStages(): MergedStage[] {
    const stages = this.mergedStages;
    if (stages.length < 2) {
      return stages;
    }
    const penultimate = stages[stages.length - 2];
    return [
      ...stages.slice(0, -2),
      { ...penultimate, steps: penultimate.steps.slice(0, -1) },
    ].filter((stage) => stage.steps.length > 0);
  }

  get readyToGoStep(): MergedStep | null {
    const stages = this.mergedStages;
    return stages.length < 2 ? null : stages[stages.length - 2].steps.at(-1) ?? null;
  }

  get finalStage(): MergedStage | null {
    const stages = this.mergedStages;
    return stages.length < 2 ? null : stages[stages.length - 1];
  }

  // What "Start a New Quest" resets, named as the menu shows it: every item after the first stage,
  // "Ready to go" included. The first stage is kept.
  get newQuestStageNames(): string[] {
    const stages = this.mainStages.slice(1).map((stage) => stage.name);
    if (this.readyToGoStep) {
      stages.push(this.readyToGoStep.header);
    }
    if (this.finalStage) {
      stages.push(this.finalStage.name);
    }
    return stages;
  }

  get isRTL(): boolean {
    return this.dir.value === 'rtl';
  }

  onStageClick(stageId: string): void {
    this.expandedStageId = this.expandedStageId === stageId ? '' : stageId;
  }

  onStepClick(stepId: string): void {
    if (this.isOnPersonalView) {
      this.stateService.pendingStepId = stepId;
      this.router.navigate(['/process']);
    } else {
      this.stateService.stepSelected$.next(stepId);
    }
    this.stepClicked.emit();
  }

  onCVUploadClick(): void {
    this.stateService.cvUploadClicked$.next();
  }

  onSwitchToArtifact(): void {
    this.stateService.switchToArtifactClicked$.next();
  }

  isFirstStageCompleted(): boolean {
    const stages = this.mergedStages;
    return stages.length > 0 && stages[0].status === StructuredProcessInstanceStatusEnum.COMPLETED;
  }

  onResetStep(step: MergedStep): void {
    this.stateService.restartStep$.next(step.id);
  }

  onResetProcess(): void {
    this.hideNewQuestCard();
    this.stateService.resetProcess$.next();
  }

  showNewQuestCard(): void {
    clearTimeout(this.newQuestCardTimer);
    // a short delay, so moving the mouse across the menu doesn't flash the card
    this.newQuestCardTimer = setTimeout(() => (this.isNewQuestCardOpen = true), 150);
  }

  // a mouse only: touch screens fire mouseenter too - after a tap, and again when the dialog closes over the
  // spot where Cancel was tapped - and the card would then stay open with nothing to close it
  onNewQuestButtonMouseEnter(): void {
    if (window.matchMedia('(hover: hover)').matches) {
      this.showNewQuestCard();
    }
  }

  // keyboard focus only: the dialog hands focus back to the button when it closes, and a mouse user
  // who just clicked Cancel shouldn't get the card
  onNewQuestButtonFocus(event: FocusEvent): void {
    if ((event.target as HTMLElement).matches(':focus-visible')) {
      this.showNewQuestCard();
    }
  }

  hideNewQuestCard(): void {
    clearTimeout(this.newQuestCardTimer);
    this.isNewQuestCardOpen = false;
  }

  // keys pressed anywhere while the card is open, so Escape closes it under the mouse as well as on focus
  onNewQuestCardKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.hideNewQuestCard();
    }
  }

  onNewQuestCardPositionChange(change: ConnectedOverlayPositionChange): void {
    this.isNewQuestCardBelow = change.connectionPair.overlayY === 'top';
  }

  onMyDriversClick(): void {
    this.currentStepId = '';
    this.router.navigate(['/personalView']);
    this.stepClicked.emit();
  }

  private updateCvUploadAnchor(): void {
    if (!this.processData?.stages?.length) {
      this.cvUploadAfterStepId = '';
      return;
    }
    const firstStage = [...this.processData.stages].sort((a, b) => a.idx - b.idx)[0];
    const steps = [...firstStage.steps].sort((a, b) => a.idx - b.idx);
    if (steps.length >= 2) {
      this.cvUploadAfterStepId = steps[steps.length - 2].id;
    } else {
      this.cvUploadAfterStepId = '';
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.newQuestCardTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
